import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { periodsApi } from '../../services/api';

export const fetchPeriods = createAsyncThunk('periods/list', async (params, { rejectWithValue }) => {
  try {
    const res = await periodsApi.list(params);
    return { items: res.data, pagination: res.pagination };
  } catch (err) {
    return rejectWithValue(err.message);
  }
});

export const fetchPeriod = createAsyncThunk('periods/get', async (id, { rejectWithValue }) => {
  try {
    const res = await periodsApi.get(id);
    return res.data;
  } catch (err) {
    return rejectWithValue(err.message);
  }
});

export const createPeriod = createAsyncThunk('periods/create', async (payload, { rejectWithValue }) => {
  try {
    const res = await periodsApi.create(payload);
    return res.data;
  } catch (err) {
    return rejectWithValue(err.message);
  }
});

// Donem kopyala: kaynak donemin tum aktif gorevlerini (RuleSet'leri) yeni
// donem altina kopyalar. Admin her ay sifirdan baslamak zorunda kalmaz.
// Backend: POST /rule-engine/periods/{source_id}/clone?clone_tasks=true
export const clonePeriod = createAsyncThunk(
  'periods/clone',
  async ({ sourcePeriodId, payload, cloneTasks = true }, { rejectWithValue }) => {
    try {
      const res = await periodsApi.clone(sourcePeriodId, payload, cloneTasks);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

export const updatePeriod = createAsyncThunk(
  'periods/update',
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await periodsApi.update(id, payload);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

export const activatePeriod = createAsyncThunk('periods/activate', async (id, { rejectWithValue }) => {
  try {
    const res = await periodsApi.activate(id);
    return res.data;
  } catch (err) {
    return rejectWithValue(err.message);
  }
});

export const closePeriod = createAsyncThunk('periods/close', async (id, { rejectWithValue }) => {
  try {
    const res = await periodsApi.close(id);
    return res.data;
  } catch (err) {
    return rejectWithValue(err.message);
  }
});

export const deletePeriod = createAsyncThunk('periods/delete', async (id, { rejectWithValue }) => {
  try {
    await periodsApi.delete(id);
    return id; // sadece id dönüyor — reducer listeden kaldırır
  } catch (err) {
    return rejectWithValue(err.message);
  }
});

/**
 * Evaluate a period. Accepts either `id` (legacy) or `{ id, dry_run }` so
 * callers can request a preview run without touching evaluation/leaderboard
 * tables (backend wraps the work in a SAVEPOINT + rollback).
 */
export const reopenPeriod = createAsyncThunk('periods/reopen', async (id, { rejectWithValue }) => {
  try {
    const res = await periodsApi.reopen(id);
    return res.data;
  } catch (err) {
    return rejectWithValue(err.message);
  }
});

export const evaluatePeriod = createAsyncThunk(
  'periods/evaluate',
  async (arg, { rejectWithValue }) => {
    const id = typeof arg === 'object' && arg !== null ? arg.id : arg;
    const dryRun = typeof arg === 'object' && arg !== null
      ? Boolean(arg.dry_run ?? arg.dryRun)
      : false;
    try {
      const res = await periodsApi.evaluate(id, { dry_run: dryRun });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

const initialState = {
  items: [],
  selected: null,
  status: 'idle',
  mutateStatus: 'idle',
  evaluateStatus: 'idle',
  error: null,
  pagination: { total: 0, page: 1, size: 50 },
};

const periodsSlice = createSlice({
  name: 'periods',
  initialState,
  reducers: {
    clearSelected(state) {
      state.selected = null;
    },
    resetStatus(state) {
      state.mutateStatus = 'idle';
      state.evaluateStatus = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPeriods.pending, (s) => { s.status = 'loading'; s.error = null; })
      .addCase(fetchPeriods.fulfilled, (s, a) => {
        s.status = 'succeeded';
        s.items = a.payload.items;
        if (a.payload.pagination) s.pagination = a.payload.pagination;
      })
      .addCase(fetchPeriods.rejected, (s, a) => { s.status = 'failed'; s.error = a.payload; })

      .addCase(fetchPeriod.fulfilled, (s, a) => { s.selected = a.payload; })

      .addCase(createPeriod.pending, (s) => { s.mutateStatus = 'loading'; })
      .addCase(createPeriod.fulfilled, (s, a) => {
        s.mutateStatus = 'succeeded';
        s.items.unshift(a.payload);
      })
      .addCase(createPeriod.rejected, (s, a) => { s.mutateStatus = 'failed'; s.error = a.payload; })

      .addCase(clonePeriod.pending, (s) => { s.mutateStatus = 'loading'; })
      .addCase(clonePeriod.fulfilled, (s, a) => {
        s.mutateStatus = 'succeeded';
        s.items.unshift(a.payload);
      })
      .addCase(clonePeriod.rejected, (s, a) => { s.mutateStatus = 'failed'; s.error = a.payload; })

      .addCase(updatePeriod.fulfilled, (s, a) => {
        const i = s.items.findIndex((p) => p.id === a.payload.id);
        if (i >= 0) s.items[i] = a.payload;
        if (s.selected?.id === a.payload.id) s.selected = a.payload;
      })

      .addCase(activatePeriod.fulfilled, (s, a) => {
        const i = s.items.findIndex((p) => p.id === a.payload.id);
        if (i >= 0) s.items[i] = a.payload;
        if (s.selected?.id === a.payload.id) s.selected = a.payload;
      })
      .addCase(closePeriod.fulfilled, (s, a) => {
        const i = s.items.findIndex((p) => p.id === a.payload.id);
        if (i >= 0) s.items[i] = a.payload;
        if (s.selected?.id === a.payload.id) s.selected = a.payload;
      })

      .addCase(reopenPeriod.fulfilled, (s, a) => {
        const i = s.items.findIndex((p) => p.id === a.payload.id);
        if (i >= 0) s.items[i] = a.payload;
        if (s.selected?.id === a.payload.id) s.selected = a.payload;
      })

      .addCase(evaluatePeriod.pending, (s) => { s.evaluateStatus = 'loading'; })
      .addCase(evaluatePeriod.fulfilled, (s) => { s.evaluateStatus = 'succeeded'; })
      .addCase(evaluatePeriod.rejected, (s, a) => { s.evaluateStatus = 'failed'; s.error = a.payload; })

      .addCase(deletePeriod.fulfilled, (s, a) => {
        s.items = s.items.filter((p) => p.id !== a.payload);
        if (s.selected?.id === a.payload) s.selected = null;
      });
  },
});

export const { clearSelected, resetStatus } = periodsSlice.actions;
export const selectPeriods = (s) => s.periods.items;
export const selectActivePeriod = (s) =>
  s.periods.items.find((p) => String(p.status).toLowerCase() === 'active') || null;

// Aktif yoksa en son kapalı dönemi de döndürür — Leaderboard gibi yerlerde
// dönem kapalı olsa bile sıralama gösterilsin.
export const selectDefaultPeriod = (s) => {
  const items = s.periods.items;
  return (
    items.find((p) => String(p.status).toLowerCase() === 'active') ||
    items
      .filter((p) => String(p.status).toLowerCase() === 'closed')
      .sort((a, b) => (b.end_date || '').localeCompare(a.end_date || ''))[0] ||
    null
  );
};
export const selectPeriodById = (id) => (s) => s.periods.items.find((p) => p.id === Number(id));
export default periodsSlice.reducer;
